/**
 * Utility per Google Maps API
 */

// Mappa dei tipi di luogo di Google Maps ai corrispondenti numeri
const TYPE_MAP: Record<string, number> = {
    "administrative_area_level_1": 1, // Regione
    "administrative_area_level_2": 2, // Provincia
    "natural_feature": 3, // Zona
    "administrative_area_level_3": 4, // Comune
    "locality": 5, // Località
    "street_address": 6, // Indirizzo
    "route": 6, // Indirizzo
    "other": 7, // Altro
    "postal_code": 8 // CAP
};

/**
 * Converte il tipo numerico in una dicitura.
 */
export function getAddressType(type: number): string {
    const typeMap: Record<number, string> = {
        1: "Regione ",
        2: "Provincia ",
        3: "Zona ",
        4: "Comune ",
        5: "Località ",
        6: "Indirizzo ",
        7: "",
        8: "CAP ",
    };

    return typeMap[type] || "Altro ";
}

/**
 * Recupera il valore di un componente dell'indirizzo.
 */
export function getComponent(components: any[], type: string): string {
    const component = components.find(c => c.types.includes(type));
    return component ? component.long_name : '';
}

/**
 * Ottiene le previsioni dei luoghi dall'API di Google Maps.
 */
export async function getPlacePredictions(
    query: string,
    autocompleteService: any
): Promise<any[]> {
    return new Promise((resolve, reject) => {
        autocompleteService.getPlacePredictions({
            input: query,
            region: "it",
            language: "it",
            componentRestrictions: {
                country: "it"
            },
            types: ['street_address', 'political', 'route', 'natural_feature', 'postal_code']
        }, (predictions: any, status: any) => {
            if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && predictions) {
                resolve(predictions);
            } else {
                reject(status);
            }
        });
    });
}

/**
 * Ottiene le previsioni dei luoghi dall'API di Google Maps con opzioni personalizzate.
 */
export async function getPlacePredictionsWithLocation(
    query: string,
    autocompleteService: any,
    options: any = {}
): Promise<any[]> {
    return new Promise((resolve, reject) => {
        autocompleteService.getPlacePredictions({
            input: query,
            region: "it",
            language: "it",
            componentRestrictions: {
                country: "it"
            },
            types: ['street_address', 'political', 'route', 'natural_feature', 'postal_code'],
            ...options
        }, (predictions: any, status: any) => {
            if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && predictions) {
                resolve(predictions);
            } else {
                reject(status);
            }
        });
    });
}

/**
 * Ottiene i dettagli di un luogo dall'API di Google Maps.
 */
export async function getPlaceDetails(
    placeId: string,
    placeService: any
): Promise<any> {
    return new Promise((resolve, reject) => {
        placeService.getDetails({ placeId }, (place: any, status: any) => {
            if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && place) {
                resolve(place);
            } else {
                reject(status);
            }
        });
    });
}

/**
 * Controlla se la query di ricerca è valida.
 */
export function isValidSearchQuery(query: string): boolean {
    return query.length >= 3 && !/^via\s*$/i.test(query);
}

/**
 * Verifica se il tipo di luogo è un indirizzo.
 */
export function isAddressType(place: any): boolean {
    return place.types?.includes('street_address') || place.types?.includes('route') || false;
}

interface NormalizedPrediction {
    value: string;
    label: string;
    firstTerm: string;
    type: number;
}

/**
 * Normalizza le previsioni dei luoghi.
 */
export function normalizePredictions(predictions: any[]): NormalizedPrediction[] {
    return predictions.map(prediction => ({
        value: prediction.place_id,
        label: prediction.description,
        firstTerm: prediction.terms[0].value,
        type: normalizeType(
            prediction.types,
            prediction.terms[0].value,
            prediction.terms.length > 1 ? prediction.terms[1].value : ''
        )
    })).sort((a, b) => a.type - b.type);
}

/**
 * Normalizza il tipo di luogo in base ai tipi di Google Maps.
 */
export function normalizeType(types: string[], firstTerm: string, secondTerm: string): number {
    // Caso speciale per "Comune" che richiede una combinazione di tipi
    if (types.includes("locality") && types.includes("political")) {
        return 4; // Comune
    }

    // Itera sui tipi di luogo e restituisce il numero corrispondente se trovato nella mappa
    for (const type of types) {
        if (TYPE_MAP[type] !== undefined) {
            return TYPE_MAP[type];
        }
    }

    return 7; // Altro
}

/**
 * Normalizza il nome del comune.
 */
export function normalizeComune(comune: string): string {
    let com = comune.toLowerCase();

    // Rimuove le diciture "Comune" o "Comune di"
    com = com.replace(/^(comune di |comune)/, '').trim();

    // Rimuove i caratteri dopo la virgola
    const commaIndex = com.indexOf(',');
    if (commaIndex !== -1) {
        com = com.substring(0, commaIndex).trim();
    }

    // Capitalizza la prima lettera
    com = com.charAt(0).toUpperCase() + com.slice(1);

    return com;
}

/**
 * Normalizza il nome della provincia.
 */
export function normalizeProvincia(provincia: string): string {
    let prov = provincia.toLowerCase();

    // Gestione speciale per Roma Capitale
    if (prov.includes("roma capitale") || prov.includes("città metropolitana di roma")) {
        return "Roma";
    }

    // Rimuove i prefissi comuni dai nomi delle province
    prov = prov.replace(/^(provincia di |provincia del |provincia dell' |provincia della |provincia autonoma di |città metropolitana di |libero consorzio comunale di |provincia|città metropolitana|libero consorzio comunale)/, '').trim();

    // Mappa delle province speciali
    const provinceSpeciali: Record<string, string> = {
        "verbania": "Verbano-Cusio-Ossola",
        "valle d'aosta": "Valle d'Aosta/Vallée d'Aoste",
        "calabria": "Reggio di Calabria",
        "calábria": "Reggio di Calabria",
        "reggio calabria": "Reggio di Calabria",
        "reggio emilia": "Reggio nell'Emilia",
        "monza e brianza": "Monza e della Brianza",
        "monza e della brianza": "Monza e della Brianza",
        "massa e carrara": "Massa-Carrara",
        "massa-carrara": "Massa-Carrara",
        "la spezia": "La Spezia",
        "spezia": "La Spezia",
        "l'aquila": "L'Aquila",
        "aquila": "L'Aquila",
        "bolzano": "Bolzano/Bozen",
        "bozen": "Bolzano/Bozen",
        "bolzano - alto adige": "Bolzano/Bozen"
    };

    // Normalizza i nomi delle province speciali
    if (provinceSpeciali[prov]) {
        prov = provinceSpeciali[prov];
    }

    // Capitalizza la prima lettera
    prov = prov.charAt(0).toUpperCase() + prov.slice(1);

    return prov;
}

/**
 * Verifica se una stringa rappresenta un possibile CAP italiano (esattamente 5 cifre).
 */
export function isPossibleItalianCAP(value: string): boolean {
    const isNumeric = /^\d+$/.test(value);
    return isNumeric && value.length === 5;
}

// Export default per compatibilità con import vecchio stile
const GoogleMapsUtility = {
    getAddressType,
    getComponent,
    getPlacePredictions,
    getPlacePredictionsWithLocation,
    getPlaceDetails,
    isValidSearchQuery,
    isAddressType,
    normalizePredictions,
    normalizeType,
    normalizeComune,
    normalizeProvincia,
    isPossibleItalianCAP
};

export default GoogleMapsUtility;
